import { useState } from "react";
import { STATUS_INFO } from "@/lib/c";

export const Icon = ({ name, size = 22, color, className = "" }) => (
  <i className={`mdi mdi-${name} ${className}`} style={{ fontSize: size, color, lineHeight: 1 }} aria-hidden />
);

export const StatusPill = ({ status, big, testid }) => {
  const s = STATUS_INFO[status] || STATUS_INFO.menunggu_verifikasi;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full font-extrabold ${big ? "px-3 py-1.5 text-[15px]" : "px-2.5 py-1 text-xs"}`} style={{ background: s.bg, color: s.fg }}>
      <Icon name={s.icon} size={big ? 18 : 14} />{s.label}
    </span>
  );
};

export const Chip = ({ label, icon, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`chip ${active ? "on" : ""}`}>{icon && <Icon name={icon} size={18} />}{label}</button>
);

export const Section = ({ title, icon, children, testid }) => (
  <div className="card space-y-3" data-testid={testid}>
    <div className="flex items-center gap-2">
      <span className="w-8 h-8 rounded-xl bg-[var(--brand3)] flex items-center justify-center"><Icon name={icon} size={18} color="var(--brand)" /></span>
      <h3 className="text-[17px] font-black">{title}</h3>
    </div>
    {children}
  </div>
);

export const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[var(--divider)] last:border-0">
    <span className="text-[var(--muted)] font-semibold">{k}</span><span className="font-bold text-right whitespace-pre-wrap">{v}</span>
  </div>
);

export function Field({ label, value, onChange, placeholder, multiline, error, testid, readOnly, type = "text" }) {
  const P = multiline ? "textarea" : "input";
  return (
    <label className="flex-1 block space-y-1.5">
      <span className="text-sm font-extrabold">{label}</span>
      <P data-testid={testid} type={type} value={value} readOnly={readOnly} placeholder={placeholder} onChange={(e) => onChange?.(e.target.value)}
        className={`input ${multiline ? "min-h-[96px]" : ""} ${error ? "!border-[var(--error)]" : ""} ${readOnly ? "!bg-[var(--brand3)] !border-[var(--sec)] font-extrabold text-[var(--brand)]" : ""}`} />
      {error && <span className="text-sm text-[var(--error)]">{error}</span>}
    </label>
  );
}

export function Sheet({ open, onClose, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose} data-testid={`${testid}-backdrop`}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-[28px] sm:rounded-[28px] p-5 space-y-3 max-h-[90vh] overflow-y-auto fade-up" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        {children}
      </div>
    </div>
  );
}

export function Confirm({ open, title, text, okLabel = "Ya, Hapus", onOk, onClose, busy, testid = "confirm-sheet" }) {
  return (
    <Sheet open={open} onClose={onClose} testid={testid}>
      <h3 className="text-xl font-black">{title}</h3>
      <p className="text-[var(--muted)]">{text}</p>
      <div className="flex gap-2 pt-2">
        <button className="btn bg-[var(--tert)] flex-1" onClick={onClose} data-testid={`${testid}-cancel`}>Batal</button>
        <button className="btn flex-1 bg-[var(--error)] text-white" disabled={busy} onClick={onOk} data-testid={`${testid}-ok`}>{okLabel}</button>
      </div>
    </Sheet>
  );
}

export function PublicFrame({ children }) {
  return (
    <div className="min-h-screen">
      <header className="bg-[var(--brand)] text-white p-4 flex items-center gap-3">
        <div className="w-11 h-12 bg-white rounded-xl flex items-center justify-center"><img src="/assets/images/logo-mimika.png" alt="Logo Kabupaten Mimika" className="w-8 h-10 object-contain" /></div>
        <div className="flex-1"><b className="font-black">SIAP PAPA BERSERI</b><p className="text-xs opacity-80">Dinas Lingkungan Hidup Kabupaten Mimika · Portal Publik</p></div>
        <a href="/publik/news" className="btn !py-2 bg-white/15 text-white text-sm" data-testid="public-nav-news">NEWS</a>
        <a href={`/publik/rekap/${new Date().getFullYear()}`} className="hidden sm:inline-flex btn !py-2 bg-white/15 text-white text-sm" data-testid="public-nav-rekap">Rekap</a>
      </header>
      <div className="p-4 max-w-3xl mx-auto space-y-4" data-testid="public-page">{children}</div>
      <p className="text-center text-xs text-[var(--muted)] font-bold pb-6">Mimika Bersih, Lingkungan Lestari, Masa Depan Berseri</p>
    </div>
  );
}

export const Spinner = () => <div className="flex justify-center py-8"><div className="w-8 h-8 rounded-full border-4 border-[var(--sec)] border-t-[var(--brand)] animate-spin" /></div>;

export const Empty = ({ icon = "file-document-outline", title, text }) => (
  <div className="card text-center py-10 space-y-2"><Icon name={icon} size={36} color="var(--muted)" /><p className="font-black text-lg">{title}</p>{text && <p className="text-[var(--muted)]">{text}</p>}</div>
);

export function useBusy() {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { return await fn(); } finally { setBusy(false); } };
  return [busy, run];
}
